import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { authService } from "@/services/authService";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await authService.login({ email, password });
      setSession(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}!`);
      navigate(location.state?.from || "/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between bg-primary text-primary-foreground p-12 text-grain">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg">
          <GraduationCap className="h-6 w-6" /> Learn Me
        </Link>
        <div>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter leading-none">Welcome back.</h1>
          <p className="mt-4 text-primary-foreground/80 max-w-sm">Your learning library is waiting. Pick up where you left off.</p>
        </div>
        <span className="text-primary-foreground/60 text-sm">© 2026 Learn Me</span>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="font-display text-3xl font-bold tracking-tight mb-1">Log in</h2>
          <p className="text-muted-foreground text-sm mb-8">Enter your details to continue.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-none h-11" placeholder="you@example.com" data-testid="login-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Password</Label>
              <PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-none h-11" placeholder="••••••••" data-testid="login-password-input"
                toggleTestId="login-password-toggle" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full mt-6 rounded-none h-11 bg-primary hover:opacity-90" data-testid="login-submit-button">
            {loading ? "Logging in..." : "Log in"}
          </Button>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            No account? <Link to="/register" className="text-primary font-semibold" data-testid="goto-register">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
